import React from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Theme,
  Box,
  IconButton,
} from "@mui/material";
import AppTableHeaderCell from "../../common/pages/AppTableHeaderCell";
import { StyledTableRow } from "../../../../styles/Shipmentstyles";
import AppTableRowCell from "../../common/pages/AppTableRowCell";
import CustomTypography from "../../common/form/CustomTypography";
import TableState from "../../common/table-states/TableState";
import pencil_uderline from "../../../../assets/pencil_uderline.png";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/en-gb";
import { ProjectInventoryOpsProduct } from "../../../../models/ProjectManagement/ProjectInventoryProduct";

interface InventoryOpsTableProps {
  maxHeight?: number;
  minHeight?: number;
  handleNavigateById: (id: number) => void;
  handlePencilClick: (
    productId: number,
    taskName: string,
    currentDate: Dayjs | null
  ) => void;
  products: ProjectInventoryOpsProduct[];
  isLoading: boolean;
  archived?: number;
}

const InventoryOpsTable: React.FC<InventoryOpsTableProps> = ({
  maxHeight = window.innerHeight - 350,
  minHeight = window.innerHeight - 350,
  handleNavigateById,
  handlePencilClick,
  products,
  isLoading,
  archived,
}) => {
  const theme = useTheme<Theme>();

  const getProductStatus = (product: ProjectInventoryOpsProduct) => {
    const taskValues = Object.values(product.completed_tasks || {});
    const totalTasks = taskValues.length;
    const completedTasks = taskValues.reduce(
      (count, task) => count + (task ? 1 : 0),
      0
    );

    if (completedTasks === totalTasks) return "Completed";
    if ((product.raw_inward_qty ?? 0) > 0 || completedTasks > 0)
      return "In Progress";
    return "Pending";
  };

  return (
    <TableContainer sx={{ minHeight, maxHeight }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <AppTableHeaderCell
              minWidth="0.75rem"
              fontSize="0.75rem"
              fontColor={theme.palette.customColors.blackBodyBackgorund}
              sx={{ paddingLeft: 2 }}
              txtAlign="left"
            >
              Product <br /> Name
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="0.75rem"
              fontSize="0.75rem"
              fontColor={theme.palette.customColors.blackBodyBackgorund}
              txtAlign="left"
            >
              Product <br /> Status
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="0.75rem"
              fontSize="0.75rem"
              fontColor={theme.palette.customColors.blackBodyBackgorund}
              txtAlign="left"
            >
              Received In <br /> Warehouse
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="0.75rem"
              fontSize="0.75rem"
              fontColor={theme.palette.customColors.blackBodyBackgorund}
              txtAlign="left"
            >
              1st Quality <br /> Check
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="0.75rem"
              fontSize="0.75rem"
              fontColor={theme.palette.customColors.blackBodyBackgorund}
              txtAlign="left"
            >
              Out Of <br />
              Printing
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="0.75rem"
              fontSize="0.75rem"
              fontColor={theme.palette.customColors.blackBodyBackgorund}
              txtAlign="left"
            >
              Received in <br />
              Warehouse
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="0.75rem"
              fontSize="0.75rem"
              fontColor={theme.palette.customColors.blackBodyBackgorund}
              txtAlign="center"
            >
              2nd Quality <br />
              Check
            </AppTableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.length ? (
            products.map((item) => {
              const status = getProductStatus(item);
              const taskEntries = Object.entries(item?.completed_tasks || {});
              const qtyMap: Record<string, number | undefined> = {
                raw_inward: item.raw_inward_qty,
                raw_inward_qc: item.raw_inward_qc_rejection_qty,
                print_outward: item.print_outward_qty,
                print_inward: item.print_inward_qty,
                print_inward_qc: item.print_inward_qc_rejection_qty,
              };

              return (
                <StyledTableRow key={item.id}>
                  <AppTableRowCell
                    fontSize="1rem"
                    textAlign="left"
                    sx={{
                      borderBottom: "none !important",
                      paddingLeft: 0,
                      paddingRight: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "start",
                        gap: "1.781rem",
                        paddingLeft: "10px",
                      }}
                    >
                      <Box>
                        <img
                          style={{
                            maxHeight: "2.875rem",
                            minHeight: "2.875rem",
                            maxWidth: "2.875rem",
                            minWidth: "2.875rem",
                          }}
                          src={
                              item.mockups?.length
                                ? item.mockups.find((m) => m.priority === 1)?.image_url ||
                                  item.mockups[0].image_url
                                : item.image_url || "path/to/default/image.png"
                            }
                          alt={item?.name || "Default Image"}
                        />
                      </Box>
                      <Box
                        sx={{
                          whiteSpace: "pre-line",
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                        onClick={() => handleNavigateById(item.id)}
                      >
                        {item.name}
                        <CustomTypography
                          variant="body3"
                          color={theme.palette.customColors.blackBodyBackgorund}
                        >
                          Quantity: {item.quantity}
                        </CustomTypography>
                      </Box>
                    </Box>
                  </AppTableRowCell>
                  <AppTableRowCell
                    sx={{
                      fontSize: "0.8rem",
                      borderBottom: "none !important",
                    }}
                  >
                    <span
                      style={{
                        backgroundColor:
                          status === "Completed"
                            ? theme.palette.mintGreen?.light
                            : status === "In Progress"
                            ? theme.palette.purple?.light
                            : theme.palette.customColors.greyBodyBackground,
                        borderRadius: "5px",
                        padding: "3px 3px",
                        display: "inline-flex",
                        justifyContent: "center",
                        minWidth: "100px",
                      }}
                    >
                      {status}
                    </span>
                  </AppTableRowCell>

                  {taskEntries.map(([taskName, taskValue], idx, arr) => {
                    const currentDate = taskValue
                      ? dayjs(taskValue as unknown as string)
                      : null;

                    const product = products.find(
                      (prod) => prod.id === item.id
                    );
                    const hasPurchaseOrders =
                      taskName === "raw_inward"
                        ? (product?.purchase_orders?.length ?? 0) > 0
                        : (product?.print_inward_timelines?.length ?? 0) > 0;

                    const displayExpectedDate = hasPurchaseOrders
                      ? taskName === "raw_inward"
                        ? product?.purchase_orders?.reduce((latest, po) => {
                            const poDate = po.expected_delivery_date
                              ? dayjs(po.expected_delivery_date)
                              : null;
                            return poDate && (!latest || poDate.isAfter(latest))
                              ? poDate
                              : latest;
                          }, null as Dayjs | null)
                        : product?.print_inward_timelines?.reduce(
                            (latest, timeline) => {
                              const timelineDate = timeline.timeline
                                ? dayjs(timeline.timeline)
                                : null;
                              return timelineDate &&
                                (!latest || timelineDate.isAfter(latest))
                                ? timelineDate
                                : latest;
                            },
                            null as Dayjs | null
                          )
                      : null;

                    return (
                      <AppTableRowCell
                        key={idx}
                        sx={{
                          textAlign: "center",
                          position: "relative",
                          padding: 0,
                          borderBottom: "none !important",
                          width: "12%",
                        }}
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          position="relative"
                          width="100%"
                        >
                          {idx > 0 && (
                            <Box
                              sx={{
                                position: "absolute",
                                width: "50%",
                                height: "2px",
                                backgroundColor:
                                  theme.palette.customColors
                                    .grayBackGroundColor,
                                left: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 0,
                              }}
                            />
                          )}
                          {idx < arr.length - 1 && (
                            <Box
                              sx={{
                                position: "absolute",
                                width: "50%",
                                height: "2px",
                                backgroundColor:
                                  theme.palette.customColors
                                    .grayBackGroundColor,
                                right: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 0,
                              }}
                            />
                          )}

                          <Box
                            sx={{
                              zIndex: 1,
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              backgroundColor:
                                theme.palette.customColors.grayBackGroundColor,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <TableState
                              state={taskValue}
                              qty={
                                !taskValue
                                  ? qtyMap[taskName] ?? undefined
                                  : undefined
                              }
                            />
                          </Box>

                          {(taskName === "raw_inward" ||
                            taskName === "print_inward") &&
                            hasPurchaseOrders && (
                              <Box
                                sx={{
                                  fontSize: "0.7rem",
                                  color: theme.palette.text.primary,
                                  textAlign: "center",
                                  whiteSpace: "nowrap",
                                  marginTop: "5px",
                                  position: "absolute",
                                  bottom: "-20px",
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                {displayExpectedDate ? (
                                  <CustomTypography
                                    variant="body4"
                                    color={theme.palette.text.secondary}
                                  >
                                    {`${displayExpectedDate.format(
                                      "DD MMM, YYYY"
                                    )}`}
                                  </CustomTypography>
                                ) : (
                                  <CustomTypography
                                    variant="body4"
                                    color={theme.palette.text.secondary}
                                  >
                                    Set date
                                  </CustomTypography>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handlePencilClick(
                                      item.id,
                                      taskName,
                                      currentDate
                                    )
                                  }
                                  sx={{ padding: 0 }} 
                                  disabled={archived === 1}
                                >
                                  <img
                                    src={pencil_uderline}
                                    alt="edit-icon"
                                    style={{ width: "12px", height: "12px" }}
                                  />
                                </IconButton>
                              </Box>
                            )}
                        </Box>
                      </AppTableRowCell>
                    );
                  })}
                </StyledTableRow>
              );
            })
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                paddingTop: 15,
              }}
            >
              <CustomTypography
                color={theme.palette.customColors.greyTextColor}
                variant="body2"
              >
                NO PRODUCTS FOUND!
              </CustomTypography>
            </Box>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InventoryOpsTable;
